import { useState, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useTranslation } from '../../i18n';
import type { InventoryItem, UpdateInventoryItemInput } from '@siliconvault/shared';
import styles from './AiReviewModal.module.css';

interface AnalysisData {
  name: string;
  category: string;
  platform: string;
  subcategory: string | null;
  condition: string;
  videoSystem: string | null;
  description: string;
  confidence: 'high' | 'medium' | 'low';
  cpu?: string | null;
  memory?: string | null;
  launchDate?: string | null;
  launchPrice?: string | null;
  launchPriceCurrency?: string | null;
  launchPriceInflationAdjusted?: string | null;
}

interface FieldDiff {
  key: string;
  label: string;
  current: string;
  suggested: string;
  isMetadata?: boolean;
}

interface AiReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentItem: InventoryItem;
  suggestions: AnalysisData;
  onAccept: (data: UpdateInventoryItemInput) => void;
  isApplying: boolean;
}

const TOP_LEVEL_FIELDS: { key: keyof AnalysisData; labelKey: string }[] = [
  { key: 'name', labelKey: 'itemDetail.name' },
  { key: 'category', labelKey: 'itemDetail.category' },
  { key: 'platform', labelKey: 'itemDetail.platform' },
  { key: 'subcategory', labelKey: 'itemDetail.subcategory' },
  { key: 'condition', labelKey: 'itemDetail.condition' },
  { key: 'description', labelKey: 'itemDetail.description' },
];

const METADATA_FIELDS: { key: keyof AnalysisData; labelKey: string }[] = [
  { key: 'cpu', labelKey: 'itemDetail.cpu' },
  { key: 'memory', labelKey: 'itemDetail.memory' },
  { key: 'launchDate', labelKey: 'itemDetail.launchInfo' },
  { key: 'launchPrice', labelKey: 'itemDetail.launchPrice' },
  { key: 'launchPriceCurrency', labelKey: 'itemDetail.reviewLaunchCurrency' },
  { key: 'launchPriceInflationAdjusted', labelKey: 'itemDetail.inflationAdjusted' },
];

function normalize(val: unknown): string {
  if (val === null || val === undefined || val === '') return '';
  return String(val).trim();
}

export function AiReviewModal({
  isOpen,
  onClose,
  currentItem,
  suggestions,
  onAccept,
  isApplying,
}: AiReviewModalProps) {
  const { t } = useTranslation();
  const metadata = (currentItem as any).metadata ?? {};

  const diffs = useMemo(() => {
    const result: FieldDiff[] = [];

    for (const { key, labelKey } of TOP_LEVEL_FIELDS) {
      const current = normalize((currentItem as any)[key]);
      const suggested = normalize(suggestions[key]);
      if (suggested && current !== suggested) {
        result.push({ key, label: t(labelKey as any), current, suggested });
      }
    }

    for (const { key, labelKey } of METADATA_FIELDS) {
      const current = normalize(metadata[key]);
      const suggested = normalize(suggestions[key]);
      if (suggested && current !== suggested) {
        result.push({ key, label: t(labelKey as any), current, suggested, isMetadata: true });
      }
    }

    return result;
  }, [currentItem, suggestions, metadata, t]);

  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(diffs.map((d) => [d.key, true]))
  );

  const allSelected = diffs.length > 0 && diffs.every((d) => selected[d.key]);
  const noneSelected = diffs.every((d) => !selected[d.key]);

  function toggleAll() {
    const newVal = !allSelected;
    setSelected(Object.fromEntries(diffs.map((d) => [d.key, newVal])));
  }

  function handleApply() {
    const update: UpdateInventoryItemInput = {};
    const metadataUpdate: Record<string, unknown> = { ...metadata };

    for (const diff of diffs) {
      if (!selected[diff.key]) continue;

      if (diff.isMetadata) {
        metadataUpdate[diff.key] = suggestions[diff.key as keyof AnalysisData];
      } else {
        (update as any)[diff.key] = suggestions[diff.key as keyof AnalysisData];
      }
    }

    // Only include metadata if we changed any metadata fields
    if (diffs.some((d) => d.isMetadata && selected[d.key])) {
      update.metadata = metadataUpdate;
    }

    onAccept(update);
  }

  const confidenceClass =
    suggestions.confidence === 'high'
      ? styles.confidenceHigh
      : suggestions.confidence === 'medium'
        ? styles.confidenceMedium
        : styles.confidenceLow;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('itemDetail.reviewTitle')}
      maxWidth="1080px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('itemDetail.reviewDismiss')}
          </Button>
          <Button
            onClick={handleApply}
            disabled={noneSelected || isApplying}
            loading={isApplying}
          >
            {t('itemDetail.reviewAcceptSelected')}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={`${styles.confidence} ${confidenceClass}`}>
          {t('itemDetail.reviewConfidence')}: {suggestions.confidence}
        </div>

        {diffs.length === 0 ? (
          <div className={styles.noChanges}>{t('itemDetail.reviewNoChanges')}</div>
        ) : (
          <>
            <label className={styles.selectAllRow} onClick={toggleAll}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              {t('itemDetail.reviewSelectAll')}
            </label>

            <div className={styles.headerRow}>
              <span />
              <span>{t('itemDetail.reviewField')}</span>
              <span>{t('itemDetail.reviewCurrent')}</span>
              <span>{t('itemDetail.reviewSuggested')}</span>
            </div>

            {diffs.map((diff) => (
              <label key={diff.key} className={styles.fieldRow}>
                <input
                  type="checkbox"
                  className={styles.fieldCheckbox}
                  checked={!!selected[diff.key]}
                  onChange={() =>
                    setSelected((prev) => ({ ...prev, [diff.key]: !prev[diff.key] }))
                  }
                />
                <span className={styles.fieldLabel}>{diff.label}</span>
                <span className={styles.fieldCurrent}>{diff.current || '—'}</span>
                <span className={styles.fieldSuggested}>{diff.suggested}</span>
              </label>
            ))}
          </>
        )}
      </div>
    </Modal>
  );
}
